import axios from 'axios';
import conf from '../config/config.js';

/**
 * Get メソッド 共通処理
 * @param {string} execPath 実行パス
 * @param {object} params plain object または URLSearchParams object
 * @param {array} fields 取得したい項目
 * @param {object} [custom] カスタム設定
 * @return {Object} 実行結果
 */
export async function _getRequestCommon(
    execPath, params={}, fields=[], custom=null) {
  let result;
  if (!execPath) {
    console.log('引数が未設定のため、終了');
    return getResponse(result);
  }

  try {
    let _params = {...params};
    if (fields && fields.length > 0) {
      _params['fields'] = JSON.stringify(fields);
    }

    /** @type AxiosRequestConfig<any> */
    const _config = {
      params: _params,
      validateStatus: function(status) {
        return validHttpStatus(status);
      },
    };

    // カスタム設定が存在する場合はConfigに追加
    if (custom) {
      _config['custom'] = custom;
    }

    result = await axios.get(conf.API_URL + execPath, _config);
    return getResponse(result);
  } catch (err) {
    console.error(err);
    return getResponse(result);
  }
}

/**
 * Get メソッド
 * @param {string} execPath 実行パス
 * @param {object} params plain object または URLSearchParams object
 * @param {array} fields 取得したい項目
 * @return {Object} 実行結果
 */
export async function getRequest(
    execPath, params={}, fields=[] ) {
  return await _getRequestCommon(
      execPath, params, fields,
  );
}

/**
 * Get メソッド(通信中画像表示なし)
 * @param {string} execPath 実行パス
 * @param {object} params plain object または URLSearchParams object
 * @param {array} fields 取得したい項目
 * @return {Object} 実行結果
 */
export async function getRequestNoImage(
    execPath, params={}, fields=[] ) {
  return await _getRequestCommon(
      execPath, params, fields,
      {useLoadingImage: false},
  );
}

/**
* Post メソッド 共通処理
* @param {string} execPath 実行パス
* @param {Object} body 入力パラメータ
* @param {array} fields 取得したい項目
* @param {object} [custom] カスタム設定
* @return {Object} 実行結果
*/
export async function _postRequestCommon(
    execPath, body, fields=[], custom=null) {
  let result;
  if (!execPath) {
    console.log('引数が未設定のため、終了');
    return getResponse(result);
  }

  try {
    let _body = {...body};
    if (fields && fields.length > 0) {
      _body['fields'] = fields;
    }

    /** @type AxiosRequestConfig<any> */
    const _config = {
      validateStatus: function(status) {
        return validHttpStatus(status);
      },
    };

    // カスタム設定が存在する場合はConfigに追加
    if (custom) {
      _config['custom'] = custom;
    }

    result = await axios.post(conf.API_URL + execPath, _body, _config);
    return getResponse(result);
  } catch (err) {
    console.error(err);
    return getResponse(result);
  }
}

/**
* download(Post)メソッド
* @param {string} execPath 実行パス
* @param {Object} body 入力パラメータ
* @param {array} fields 取得したい項目
* @return {Object} 実行結果
*/
export async function downloadRequest(execPath, body, fields=[]) {
  let _body = {...body};
  if (fields && fields.length > 0) {
    _body['fields'] = fields;
  }
  /** @type AxiosRequestConfig<any> */
  const _config = {
    validateStatus: function(status) {
      return validHttpStatus(status);
    },
    responseType: 'blob',
  };
  return await axios.post(conf.API_URL + execPath, _body, _config);
}

/**
* Post メソッド
* @param {string} execPath 実行パス
* @param {Object} body 入力パラメータ
* @param {array} fields 取得したい項目
* @return {Object} 実行結果
*/
export async function postRequest(
    execPath, body, fields=[]) {
  return await _postRequestCommon(
      execPath, body, fields,
  );
}

/**
* Post メソッド(通信中画像表示なし)
* @param {string} execPath 実行パス
* @param {Object} body 入力パラメータ
* @param {array} fields 取得したい項目
* @return {Object} 実行結果
*/
export async function postRequestNoImage(
    execPath, body, fields=[]) {
  return await _postRequestCommon(
      execPath, body, fields,
      {useLoadingImage: false},
  );
}

/**
* Put メソッド
* @param {string} execPath 実行パス
* @param {Object} body 入力パラメータ
* @param {array} fields 取得したい項目
* @return {Object} 実行結果
*/
export async function putRequest(
    execPath, body, fields=[]) {
  let result;
  if (!execPath) {
    console.log('引数が未設定のため、終了');
    return getResponse(result);
  }

  try {
    let _body = {...body};
    if (fields && fields.length > 0) {
      _body['fields'] = fields;
    }
    result = await axios.put(conf.API_URL + execPath, _body, {
      validateStatus: function(status) {
        return validHttpStatus(status);
      },
    });
    return getResponse(result);
  } catch (err) {
    console.error(err);
    return getResponse(result);
  }
}

/**
* Delete メソッド
* @param {string} execPath 実行パス
* @param {Object} body 入力パラメータ
* @return {Object} 実行結果
*/
export async function deleteRequest(
    execPath, body) {
  let result;
  if (!execPath) {
    console.log('引数が未設定のため、終了');
    return getResponse(result);
  }

  try {
    result = await axios.delete(conf.API_URL + execPath, {
      data: body,
      validateStatus: function(status) {
        return validHttpStatus(status);
      },
    });
  } catch (err) {
    console.error(err);
    return getResponse(result);
  }
  return getResponse(result);
}

export const isInvalidResponse = (response) => {
  return !response?.hasOwnProperty('data');
};

export const getResponse = (response) => {
  return {
    status: response?.hasOwnProperty('status') ? response.status : '',
    statusText: response?.hasOwnProperty('statusText') ? response.statusText : '',
    data: getResponseData(response),
  };
};

export const getResponseStatus = (response) => {
  const result = {
    status: response?.hasOwnProperty('status') ? response.status : '',
    statusText: response?.hasOwnProperty('statusText') ? response.statusText : '',
  };
  return result;
};

export const getResponseData = (response) => {
  return response?.hasOwnProperty('data') ? response.data : null;
};

export const getResponseHeaders = (response) => {
  return response?.hasOwnProperty('headers') ? response.headers : null;
};

export const getResponseMethod = (response) => {
  return response.config.method;
};

export const getPath= (url) => {
  if (!url) {
    return '';
  }
  // return url.split('api')[url.split('api').length - 1];
  return url;
};

/**
 * 戻り値に画面項目追加
 * @param {array|object} result 戻り値
 * @param {array} fields 画面項目
 */
export function addFields(result, fields) {
  if (!fields || fields.length == 0) {
    return;
  }
  function setField(obj, field) {
    if (!obj?.hasOwnProperty(field)) {
      obj[field] = '';
    }
  }
  if (Array.isArray(result)) {
    const size = Object.keys(result).length;
    for (let i = 0; i < size; i++) {
      for (const field of fields) {
        setField(result[i], field);
      }
    }
  } else {
    for (const field of fields) {
      setField(result, field);
    }
  }
}

function validHttpStatus(status) {
  return (status >= axios.HttpStatusCode.Ok &&
    status < axios.HttpStatusCode.MultipleChoices) ||
    status == axios.HttpStatusCode.BadRequest;
}
